import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Calendar, Check, CalendarDays } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardBody, Table, Button, Badge, Modal, ConfirmDialog, toast
} from '../../components/common';
import { budgetApi } from '../../services/api';
import FiscalYearForm from './FiscalYearForm';
import './FiscalYear.css';

export default function FiscalYearList() {
    const queryClient = useQueryClient();
    const [showForm, setShowForm] = useState(false);
    const [activateYear, setActivateYear] = useState(null);

    const { data, isLoading } = useQuery({
        queryKey: ['fiscalYears'],
        queryFn: () => budgetApi.listFiscalYears(),
        select: (res) => res.data?.data || []
    });

    const activateMutation = useMutation({
        mutationFn: (id) => budgetApi.activateFiscalYear(id),
        onSuccess: () => {
            toast.success('Fiscal year activated successfully');
            queryClient.invalidateQueries(['fiscalYears']);
            setActivateYear(null);
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to activate');
        }
    });

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const columns = [
        {
            key: 'year',
            title: 'Year',
            dataIndex: 'year',
            render: (year, row) => (
                <div className="fy-year">
                    <CalendarDays size={18} />
                    <span>{year}</span>
                    {row.isActive && <Badge variant="success">Active</Badge>}
                </div>
            )
        },
        {
            key: 'period',
            title: 'Period',
            render: (_, row) => (
                <span className="fy-period">
                    {formatDate(row.startDate)} - {formatDate(row.endDate)}
                </span>
            )
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            render: (status) => {
                const variants = {
                    'open': 'success',
                    'closed': 'default',
                    'planned': 'warning'
                };
                return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
            }
        },
        {
            key: 'actions',
            title: '',
            width: 120,
            align: 'right',
            render: (_, row) => (
                !row.isActive && row.status !== 'closed' && (
                    <Button
                        size="sm"
                        variant="secondary"
                        icon={Check}
                        onClick={() => setActivateYear(row)}
                    >
                        Activate
                    </Button>
                )
            )
        }
    ];

    return (
        <div className="fiscal-year-page">
            <PageHeader
                title="Fiscal Year Management"
                subtitle="Manage fiscal years for budget planning"
                breadcrumbs={[
                    { label: 'Budget', path: '/budgets' },
                    { label: 'Fiscal Years' }
                ]}
                actions={
                    <Button icon={Plus} onClick={() => setShowForm(true)}>
                        Add Fiscal Year
                    </Button>
                }
            />

            <Card>
                <Table
                    columns={columns}
                    data={data || []}
                    loading={isLoading}
                    emptyText="No fiscal years found"
                    rowKey="id"
                />
            </Card>

            <Modal
                isOpen={showForm}
                onClose={() => setShowForm(false)}
                title="Add Fiscal Year"
                size="sm"
            >
                <FiscalYearForm
                    onSuccess={() => {
                        setShowForm(false);
                        queryClient.invalidateQueries(['fiscalYears']);
                    }}
                    onCancel={() => setShowForm(false)}
                />
            </Modal>

            <ConfirmDialog
                isOpen={!!activateYear}
                onClose={() => setActivateYear(null)}
                onConfirm={() => activateMutation.mutate(activateYear?.id)}
                title="Activate Fiscal Year"
                message={`Are you sure you want to activate fiscal year ${activateYear?.year}? This will deactivate the current active year.`}
                confirmText="Activate"
                loading={activateMutation.isPending}
            />
        </div>
    );
}
